const TASK_CHANGE_CHANNEL = "incontrol-task-changes";
const TASK_CHANGE_MESSAGE = "task-changed";
const TASK_CHANGE_STORAGE_KEY = "incontrol-task-change-signal";
const DEFAULT_COALESCE_MS = 250;

type BroadcastChannelLike = Pick<BroadcastChannel, "postMessage" | "addEventListener" | "removeEventListener" | "close">;

function openTaskChangeChannel(): BroadcastChannelLike | null {
  if (typeof BroadcastChannel === "undefined") return null;
  try {
    return new BroadcastChannel(TASK_CHANGE_CHANNEL);
  } catch {
    return null;
  }
}

function publishStorageSignal(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(TASK_CHANGE_STORAGE_KEY, `${Date.now()}-${Math.random()}`);
    window.localStorage.removeItem(TASK_CHANGE_STORAGE_KEY);
  } catch {
    // Storage can be unavailable in restricted or sandboxed browsing contexts.
  }
}

/**
 * Notify other same-origin browsing contexts that task-backed UI may be stale.
 * BroadcastChannel intentionally does not echo a message to its sender.
 */
export function notifyTaskChanged(): void {
  const channel = openTaskChangeChannel();
  if (channel) {
    channel.postMessage(TASK_CHANGE_MESSAGE);
    channel.close();
    return;
  }
  publishStorageSignal();
}

/**
 * Listen for task changes from other windows. A short trailing debounce turns
 * multi-mutation actions into one refresh while keeping side-by-side views live.
 */
export function subscribeToTaskChanges(
  onTaskChanged: () => void,
  coalesceMs = DEFAULT_COALESCE_MS,
): () => void {
  const channel = openTaskChangeChannel();
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const scheduleTaskChanged = () => {
    if (timeout !== undefined) clearTimeout(timeout);
    timeout = setTimeout(onTaskChanged, coalesceMs);
  };
  const handleMessage = (event: MessageEvent<unknown>) => {
    if (event.data === TASK_CHANGE_MESSAGE) scheduleTaskChanged();
  };
  const handleStorage = (event: StorageEvent) => {
    if (event.key === TASK_CHANGE_STORAGE_KEY && event.newValue !== null) {
      scheduleTaskChanged();
    }
  };

  if (channel) {
    channel.addEventListener("message", handleMessage);
  } else if (typeof window !== "undefined") {
    window.addEventListener("storage", handleStorage);
  }

  return () => {
    if (timeout !== undefined) clearTimeout(timeout);
    if (channel) {
      channel.removeEventListener("message", handleMessage);
      channel.close();
    } else if (typeof window !== "undefined") {
      window.removeEventListener("storage", handleStorage);
    }
  };
}